/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import isEqual from 'lodash.isequal';
import isUndefined from 'lodash.isundefined';
import {translate} from '@ion/core/src/locale/locale';
import {setRel} from '@ion/core/src/services/ConnectUtils';
import {
  DeviceBulkEditorFormFields
} from '@ion/core/src/types/editors';
import {
  AccountLink,
  Device,
  DeviceConfiguration,
  DeviceIdentifierType,
  DeviceType,
  Entity,
  EntityStatus
} from '@ion/core/src/types/entities';
import {toISOString} from '@ion/core/src/utils/DateUtil';
import {isValue} from '@ion/core/src/utils/StringUtil';
import FormGroupFields from '@ion/dls/src/components/forms/FormGroupFields';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {getInitialState} from '@ion/dls/src/editors/BulkDeviceEditorUtils';
import {BulkEditorType} from '@ion/dls/src/editors/BulkEditorUtils';
import DatePicker from '@ion/dls/src/editors/datetimepicker/DatePicker';
import {DeviceFormFields, isVisionDevice} from '@ion/dls/src/editors/DeviceEditorUtils';
import styles from '@ion/dls/src/editors/editorStyles';
import EntityStatusChooser from '@ion/dls/src/editors/EntityStatusChooser';
import EmbeddedForm from '@ion/dls/src/editors/forms/EmbeddedForm';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {useFormSave} from '@ion/dls/src/hooks/useFormSave';
import {useFormState} from '@ion/dls/src/hooks/useFormState';
import {useHasChange} from '@ion/dls/src/hooks/useHasChange';
import {useMergeState} from '@ion/dls/src/hooks/useMergeState';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {convertStatus} from '@ion/dls/src/utils/ComponentUtils';
import DeviceConfigurationChooser from '@ion/features/src/entities/device/DeviceConfigurationChooser';
import DeviceTypeChooser from '@ion/features/src/entities/device/DeviceTypeChooser';
import {useCurrentGlobalGroup} from '../hooks/useCurrentGlobalGroup';
import {useSanitizeAccount} from '../hooks/useSanitizeAccount';

interface DevicesBulkEditFormProps {
  onSave: (details: DevicesBulkEditDetails) => void;
  onBack?: Function;
  accountId?: number;
  isBulkAdd?: boolean;
  identifierType?: string;
  taskNickName?: string;
  setTaskNickName?: (action: string) => void;
  isSelectedGridList?: boolean;
  setSelectedIdentifierType?: (identifierType: string) => void;
}

type DevicesBulkEditDetails = {
  device: {
    deviceType?: DeviceType;
    deviceConfiguaration?: DeviceConfiguration;
    shipDate?: Date;
    status?: EntityStatus;
    account?: AccountLink;
  }
}

interface TargetType {
  name: DeviceBulkEditorFormFields;
  value: any;
  required?: boolean;
}

const styleOverrides = (theme: DLSTheme) => ({
  inputField : {
    marginBottom : '20px'
  },
  errorText : {
    margin    : 10,
    textAlign : 'center',
    color     : dlsColor('caRed')
  },
  textPadding : {
    padding : 10
  }
});

/**
 * Re-usable form with the details for Bulk Edit of a devices.
 */
export const DevicesBulkEditForm = forwardRef(({
  accountId,
  onBack,
  onSave,
  identifierType,
  taskNickName,
  setTaskNickName,
  setSelectedIdentifierType,
  isBulkAdd = false,
  isSelectedGridList = false
}: DevicesBulkEditFormProps, ref) => {
  const editorStyles                              = useExtendStyles(styles, styleOverrides);
  const classes                                   = editorStyles();
  const [openDatePickers, setOpenDatePickers]     = useMergeState();
  const [formState, setFormState]                 = useFormState(getInitialState({}, []));
  const hasChange                                 = useHasChange(formState);
  const globalGroup                               = useCurrentGlobalGroup();
  const account                                   = useSanitizeAccount(globalGroup);
  const identifierTypes                           = translate('common:editors.choosers.identifierTypes.device.options', {returnObjects: true});
  const [formError, setFormError]                 = useState(false);
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [isEditError, setIsEditError]             = useState(false);
  const [deviceDetails, setDeviceDetails]         = useState<DevicesBulkEditDetails>();

  const submitValidation = {
    [DeviceBulkEditorFormFields.DeviceType] : (value) => {
      return isBulkAdd ? !!value : true;
    },
    [DeviceBulkEditorFormFields.DeviceConfiguration] : ({value}) => {
      const deviceType = formState[DeviceBulkEditorFormFields.DeviceType]?.value;
      return deviceType ? !!(deviceType && value) : true;
    }
  };

  const serialize = (state: BulkEditorType): Device => {
    const serialized: Device = {};
    for (const [key, {value}] of Object.entries(state)) {
      if (!isUndefined(value)) {
        if (DeviceBulkEditorFormFields.Status === key) {
          serialized.status = convertStatus(value) as EntityStatus;
        } else if (DeviceBulkEditorFormFields.DeviceType === key) {
          const link = value?.href ? value : null;
          if (value) {
            serialized.type = value?.id ? {...setRel(value, 'devicetype'), title: value.name} : link;
          }
        } else if (DeviceBulkEditorFormFields.DeviceConfiguration === key) {
          const link                     = value?.href ? value : null;
          if (value) {
            serialized.deviceConfiguration = value?.id ? setRel(value, 'deviceConfiguration') : link;
          }
        } else {
          serialized[key] = value;
        }
      }
    }
    return serialized;
  };

  const onValidForm = (data, skipChangeCheck = false) => {
    const details = {device: data} as DevicesBulkEditDetails;
    if (hasChange || skipChangeCheck) {
      if (Object.keys(data).length) {
        if (!skipChangeCheck && isVisionDevice(details.device) && !isEqual(identifierType, DeviceFormFields.IMEI)) {
          if (isBulkAdd) {
            setDeviceDetails(data);
            setShowWarningDialog(true);
          } else {
            setShowWarningDialog(true);
            setIsEditError(true);
          }
        } else {
          if (isBulkAdd) {
            details.device.account = account;
            details.device.status  = EntityStatus.Enabled;
          }
          onSave(details);
        }
      } else {
        setFormError(true);
      }
    } else {
      setFormError(true);
    }
  };

  const onSubmit = useFormSave({
    validate : submitValidation,
    onSave   : onValidForm,
    serialize,
    formState,
    setFormState
  });

  useEffect(() => {
    if (hasChange) {
      setFormError(false);
    }
  }, [hasChange]);

  useEffect(() => {
    if (isBulkAdd) {
      setIsEditError(false);
    }
  }, [isBulkAdd]);

  const handleChooserChange = useCallback(
    ({name, value, required = false}: TargetType) => {
      setFormError(false);
      setFormState({
        [name] : {
          name,
          value : name === DeviceBulkEditorFormFields.ShipDate
            ? toISOString(value)
            : isValue(value, 'none') ? null : value,
          required
        }
      });
    }, []);

  const onEntityStatusChooserChange = useCallback((value) => {
    handleChooserChange({
      name : DeviceBulkEditorFormFields.Status,
      value
    });
  }, []);

  const onDeviceTypeChange = useCallback((value) => {
    handleChooserChange({
      name : DeviceBulkEditorFormFields.DeviceType,
      value
    });
    handleChooserChange({
      name     : DeviceBulkEditorFormFields.DeviceConfiguration,
      required : !!value,
      value    : null
    });
  }, []);

  function onDeviceConfigurationChange(value) {
    handleChooserChange({
      name     : DeviceBulkEditorFormFields.DeviceConfiguration,
      required : !!formState[DeviceBulkEditorFormFields.DeviceType]?.value,
      value
    });
  };

  const onShipDateChange = useCallback((value) => {
    handleChooserChange({
      name : DeviceBulkEditorFormFields.ShipDate,
      value
    });
  }, []);

  const onToggleDatePicker = useCallback(() => {
    setOpenDatePickers(
      {[DeviceBulkEditorFormFields.ShipDate]: !openDatePickers[DeviceBulkEditorFormFields.ShipDate]}
    );
  }, [openDatePickers]);

  const getIdentifierTypeLabel = (identifierType: string): string => (
    identifierTypes.find(type => type.value === identifierType)?.label
  );

  useImperativeHandle(ref, () => ({
    onSubmit
  }), [onSubmit]);

  const onProceed = useCallback(() => {
    if (isEditError) {
      onBack();
      setIsEditError(false);
    } else {
      setTaskNickName(taskNickName.replace(getIdentifierTypeLabel(identifierType), DeviceIdentifierType.IMEI));
      setSelectedIdentifierType(DeviceIdentifierType.IMEI.toLowerCase());
      onValidForm(deviceDetails, true);
    }
    setShowWarningDialog(false);
  }, [deviceDetails, taskNickName, identifierType, isEditError]);

  const onDismiss = useCallback(() => {
    setShowWarningDialog(false);
  }, []);

  return (
    <>
      <EmbeddedForm>
        {formError && (
          <div className={classes.errorText}>
            <div className={classes.textPadding}>
              {translate('common:editors.bulkActions.dialogBulkAction.bulkEditError')}
            </div>
          </div>
        )}
        <FormSection>
          <FormGroupFields>
            <FormItem className={classes.inputField}>
              <DeviceTypeChooser required={isBulkAdd}
                hasError={formState[DeviceBulkEditorFormFields.DeviceType]?.error}
                removeVisionCamera={isSelectedGridList && !isBulkAdd}
                onChange={onDeviceTypeChange} />
            </FormItem>
            <FormItem className={classes.inputField}>
              <DeviceConfigurationChooser accountId={accountId}
                readOnly={!formState[DeviceBulkEditorFormFields.DeviceType]?.value}
                deviceType={formState[DeviceBulkEditorFormFields.DeviceType]?.value}
                onChange={onDeviceConfigurationChange}
                helperText={formState[DeviceBulkEditorFormFields.DeviceConfiguration]?.error && translate('common:editors.shared.required')}
                hasError={formState[DeviceBulkEditorFormFields.DeviceConfiguration]?.error} />
            </FormItem>
            <FormItem className={classes.inputField}>
              <DatePicker closeOnClickOutside
                useDatePickerTextField
                fieldLabel={translate('admin-devices:dialog.shipDate.label')}
                placeholder={translate('admin-devices:dialog.shipDate.placeholder')}
                open={openDatePickers[DeviceBulkEditorFormFields.ShipDate]}
                toggle={onToggleDatePicker}
                onChange={onShipDateChange} />
            </FormItem>
            {!isBulkAdd && (<FormItem fullWidth className={classes.inputField}>
              <EntityStatusChooser entity={Entity.device}
                name={DeviceBulkEditorFormFields.Status}
                onChange={onEntityStatusChooserChange} />
            </FormItem>)}
          </FormGroupFields>
        </FormSection>
      </EmbeddedForm>
      {showWarningDialog && (
        <UserNotificationDialog show
          capitalize
          onAction={onProceed}
          actionLabel={translate(`common:editors.shared.${isEditError ? 'selectDevices' : 'process'}`)}
          title={translate('common:editors.bulkActions.groupAssignment.information')}
          message={translate(`common:editors.bulkActions.visionWarning.${isEditError ? 'editIdentifierMessage' : 'identifierMessage'}`)}
          dismissLabel={translate('common:editors.shared.neverMind')}
          onDismiss={onDismiss} />
      )}
    </>
  );
});

export default memo(DevicesBulkEditForm);
